import axios from 'axios';
import store from '../store';

const XSRF_HEADER = 'x-xsrf-token';
const XSRF_STORAGE_KEY = 'xsrfToken';

const axiosInstance = axios.create({
  baseURL: 'https://uat.backend.siripay.co/',
  // baseURL: 'https://backend.siripay.co/',
  // baseURL: 'http://localhost:8000/',
  withCredentials: true,
});

const getStoredXsrfToken = () => localStorage.getItem(XSRF_STORAGE_KEY);

export const setXsrfToken = (token) => {
  if (token) {
    localStorage.setItem(XSRF_STORAGE_KEY, token);
    axiosInstance.defaults.headers[XSRF_HEADER] = token;
  } else {
    localStorage.removeItem(XSRF_STORAGE_KEY);
    delete axiosInstance.defaults.headers[XSRF_HEADER];
  }
};

const getHeaderValue = (headers, name) => {
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    return headers.get(name) || null;
  }
  return headers[name] || headers[name.toLowerCase()] || null;
};

const pickCsrfToken = (...candidates) =>
  candidates.find((value) => typeof value === 'string' && value.trim()) || null;

export const persistXsrfTokenFromResponse = (response) => {
  const headerToken = getHeaderValue(response?.headers, XSRF_HEADER);
  const body = response?.data ?? response;
  const nested =
    typeof body?.data === 'object' && body?.data !== null && !Array.isArray(body.data)
      ? body.data
      : null;
  const authResponse =
    (typeof nested?.authResponse === 'object' && nested.authResponse) ||
    (typeof body?.authResponse === 'object' && body.authResponse) ||
    null;

  const token = pickCsrfToken(
    headerToken,
    body?.csrfToken,
    nested?.csrfToken,
    authResponse?.csrfToken,
    body?.[XSRF_HEADER],
    nested?.[XSRF_HEADER],
    body?.xsrfToken,
    nested?.xsrfToken
  );

  if (token) {
    setXsrfToken(token);
  }
  return token;
};

const setAuthorizationToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.Authorization;
  }
};

export const clearAuthorization = () => {
  setAuthorizationToken(null);
  setXsrfToken(null);
};

let isLoggingOut = false;

export const logoutUser = () => {
  clearAuthorization();
  localStorage.clear();
  try {
    store.dispatch({ type: 'LOG_OUT' });
  } catch (e) {
    console.error('Error dispatching LOG_OUT:', e);
  }

  if (typeof window !== 'undefined') {
    const publicRoutes = ['/', '/sign-up', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname;
    if (!publicRoutes.includes(currentPath)) {
      if (!isLoggingOut) {
        isLoggingOut = true;
        window.location.replace('/');
        setTimeout(() => {
          isLoggingOut = false;
        }, 1000);
      }
    }
  }
};

setXsrfToken(getStoredXsrfToken());

axiosInstance.interceptors.request.use((config) => {
  const xsrfToken = getStoredXsrfToken();
  if (xsrfToken) {
    config.headers[XSRF_HEADER] = xsrfToken;
  }
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization && !config.headers.authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    persistXsrfTokenFromResponse(response);
    return response;
  },
  (error) => {
    persistXsrfTokenFromResponse(error?.response);

    const status = error?.response?.status;
    const statusCode = error?.response?.data?.statusCode || error?.response?.data?.status;
    const url = String(error?.config?.url || '');

    const isAuthEndpoint =
      url.includes('/api/auth/admin/send-otp') ||
      url.includes('/api/auth/admin/verify-otp');

    if ((status === 401 || statusCode === 401) && !isAuthEndpoint) {
      logoutUser();
    }

    return Promise.reject(error);
  }
);

const executeRequest = (
  method,
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  if (manualToken) {
    setAuthorizationToken(manualToken);
  }

  const xsrfToken = getStoredXsrfToken();
  const token = manualToken || localStorage.getItem('token');
  const headers = {};
  if (xsrfToken) {
    headers[XSRF_HEADER] = xsrfToken;
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const config = {
    method,
    url: endPoint,
    data: body,
    params: method === 'GET' || method === 'DELETE' ? body : null,
    withCredentials: true,
    headers,
  };
  axiosInstance(config)
    .then((response) => {
      if (typeof successCallback === 'function') {
        successCallback(response);
      }
    })
    .catch((error) => {
      if (typeof errorCallback === 'function') {
        errorCallback(error);
      }
    });
};

const getAPI = (
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  executeRequest(
    'GET',
    endPoint,
    body,
    successCallback,
    errorCallback,
    manualToken
  );
};

const postAPI = (
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  executeRequest(
    'POST',
    endPoint,
    body,
    successCallback,
    errorCallback,
    manualToken
  );
};

const putAPI = (
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  executeRequest(
    'PUT',
    endPoint,
    body,
    successCallback,
    errorCallback,
    manualToken
  );
};

const patchAPI = (
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  executeRequest(
    'PATCH',
    endPoint,
    body,
    successCallback,
    errorCallback,
    manualToken
  );
};

const deleteAPI = (
  endPoint,
  body,
  successCallback,
  errorCallback,
  manualToken
) => {
  executeRequest(
    'DELETE',
    endPoint,
    body,
    successCallback,
    errorCallback,
    manualToken
  );
};

export { axiosInstance, getAPI, postAPI, putAPI, patchAPI, deleteAPI };
